/**
 * Rolling Collection Banner
 * Handles accessibility and repeatable scroll animations.
 */

if (!customElements.get('rolling-collection-banner')) {
    customElements.define('rolling-collection-banner', class RollingCollectionBanner extends HTMLElement {
        constructor() {
            super();
        }

        connectedCallback() {
            this.list = this.querySelector('.rolling-collections-banner__list');
            if (this.list) {
                this.list.addEventListener('focusin', this.onFocusIn.bind(this));
                this.list.addEventListener('scroll', this.updateScrollbar.bind(this));
            }

            this.initScrollbar();
            this.initIntersectionObserver();

            // Handle window resize
            window.addEventListener('resize', this.updateScrollbar.bind(this));
        }

        initScrollbar() {
            this.scrollbar = this.querySelector('.slider-scrollbar');
            this.scrollbarThumb = this.querySelector('.slider-scrollbar-thumb');

            if (!this.scrollbar || !this.scrollbarThumb || !this.list) return;

            this.scrollbarThumb.addEventListener('mousedown', this.onScrollbarDragStart.bind(this));
            this.scrollbarThumb.addEventListener('touchstart', this.onScrollbarDragStart.bind(this), { passive: false });

            // Initial update
            this.updateScrollbar();
        }

        onScrollbarDragStart(event) {
            if (event.cancelable) event.preventDefault();
            this.isDraggingScrollbar = true;
            this.startX = event.type.includes('touch') ? event.touches[0].clientX : event.clientX;
            this.startScrollLeft = this.list.scrollLeft;

            this.onScrollbarDragMoveHandler = this.onScrollbarDragMove.bind(this);
            this.onScrollbarDragEndHandler = this.onScrollbarDragEnd.bind(this);

            document.addEventListener('mousemove', this.onScrollbarDragMoveHandler);
            document.addEventListener('mouseup', this.onScrollbarDragEndHandler);
            document.addEventListener('touchmove', this.onScrollbarDragMoveHandler, { passive: false });
            document.addEventListener('touchend', this.onScrollbarDragEndHandler);

            this.scrollbarThumb.style.cursor = 'grabbing';
            document.body.style.userSelect = 'none';
        }

        onScrollbarDragMove(event) {
            if (!this.isDraggingScrollbar) return;
            if (event.cancelable) event.preventDefault();
            const clientX = event.type.includes('touch') ? event.touches[0].clientX : event.clientX;
            const deltaX = clientX - this.startX;

            const trackWidth = this.scrollbar.clientWidth;
            const thumbWidth = this.scrollbarThumb.clientWidth;
            const maxTrackScroll = trackWidth - thumbWidth;
            const maxListScroll = this.list.scrollWidth - this.list.clientWidth;

            if (maxTrackScroll > 0) {
                const deltaScroll = (deltaX / maxTrackScroll) * maxListScroll;
                this.list.scrollLeft = this.startScrollLeft + deltaScroll;
            }
        }

        onScrollbarDragEnd() {
            this.isDraggingScrollbar = false;
            document.removeEventListener('mousemove', this.onScrollbarDragMoveHandler);
            document.removeEventListener('mouseup', this.onScrollbarDragEndHandler);
            document.removeEventListener('touchmove', this.onScrollbarDragMoveHandler);
            document.removeEventListener('touchend', this.onScrollbarDragEndHandler);

            this.scrollbarThumb.style.cursor = 'grab';
            document.body.style.userSelect = '';
        }

        updateScrollbar() {
            if (!this.scrollbar || !this.scrollbarThumb || !this.list) return;

            const listScrollWidth = this.list.scrollWidth;
            const listClientWidth = this.list.clientWidth;
            const maxScrollLeft = listScrollWidth - listClientWidth;

            // Target the wrapper to hide the entire area (including margins/padding)
            const wrapper = this.scrollbar.closest('.slider-controls-wrapper');

            if (maxScrollLeft <= 1) {
                if (wrapper) wrapper.style.display = 'none';
                this.scrollbar.style.display = 'none';
                this.list.classList.add('rolling-collections-banner__list--centered');
                return;
            }

            if (wrapper) wrapper.style.display = 'flex';
            this.scrollbar.style.display = 'block';
            this.list.classList.remove('rolling-collections-banner__list--centered');

            const trackWidth = this.scrollbar.getBoundingClientRect().width;
            if (trackWidth === 0) return;

            const thumbWidth = (listClientWidth / listScrollWidth) * trackWidth;
            const effectiveThumbWidth = Math.max(thumbWidth, 40);

            const scrollLeft = this.list.scrollLeft;
            const progress = Math.max(0, Math.min(1, scrollLeft / maxScrollLeft));

            const maxThumbPosition = trackWidth - effectiveThumbWidth;
            const thumbPosition = progress * maxThumbPosition;

            this.scrollbarThumb.style.width = `${effectiveThumbWidth}px`;
            this.scrollbarThumb.style.transform = `translateX(${thumbPosition}px)`;
        }

        initIntersectionObserver() {
            const options = {
                root: null,
                rootMargin: '0px 0px -50px 0px',
                threshold: 0
            };

            this.observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    const target = entry.target;

                    if (entry.isIntersecting) {
                        // Element entered viewport: Play animation
                        target.classList.remove('rolling-trigger--offscreen');

                        // Re-apply stagger index if needed (though CSS handles this via static var)
                        // We just ensure the class removal triggers the CSS transition.

                    } else {
                        // Element left viewport: Reset animation
                        // This ensures it replays when coming back
                        target.classList.add('rolling-trigger--offscreen');
                    }
                });
            }, options);

            // Observe all trigger elements within this specific section
            const triggerElements = this.querySelectorAll('.rolling-trigger');
            triggerElements.forEach((el) => {
                this.observer.observe(el);
            });
        }

        disconnectedCallback() {
            if (this.observer) {
                this.observer.disconnect();
            }
        }

        onFocusIn(event) {
            const target = event.target;
            const item = target.closest('.rolling-collections-banner__item');

            if (item && this.list && this.list.classList.contains('rolling-collections-banner__list--scroll')) {
                item.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'center'
                });
            }
        }
    });
}
